import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Students = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    dni: '',
    email: '',
    fecha_nacimiento: '',
    edad: '',
    direccion: '',
    poblacion: '',
    codigo_postal: '',
    telefono: '',
    profesion: ''
  });

  const canEdit = user?.role === 'Administrador' || user?.role === 'Formador';

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/students`);
      setStudents(response.data);
    } catch (error) {
      toast.error('Error al cargar los alumnos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      if (dataToSend.edad) {
        dataToSend.edad = parseInt(dataToSend.edad);
      }

      if (editingStudent) {
        await axios.put(`${API_URL}/students/${editingStudent.id}`, dataToSend);
        toast.success('Alumno actualizado correctamente');
      } else {
        await axios.post(`${API_URL}/students`, dataToSend);
        toast.success('Alumno creado correctamente');
      }
      setShowDialog(false);
      resetForm();
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el alumno');
    }
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este alumno?')) return;
    
    try {
      await axios.delete(`${API_URL}/students/${studentId}`);
      toast.success('Alumno eliminado correctamente');
      fetchStudents();
    } catch (error) {
      toast.error('Error al eliminar el alumno');
    }
  };

  const openEditDialog = (student) => {
    setEditingStudent(student);
    setFormData({
      nombre: student.nombre,
      apellidos: student.apellidos,
      dni: student.dni,
      email: student.email || '',
      fecha_nacimiento: student.fecha_nacimiento || '',
      edad: student.edad || '',
      direccion: student.direccion || '',
      poblacion: student.poblacion || '',
      codigo_postal: student.codigo_postal || '',
      telefono: student.telefono || '',
      profesion: student.profesion || ''
    });
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingStudent(null);
    setFormData({
      nombre: '',
      apellidos: '',
      dni: '',
      email: '',
      fecha_nacimiento: '',
      edad: '',
      direccion: '',
      poblacion: '',
      codigo_postal: '',
      telefono: '',
      profesion: ''
    });
  };

  const filteredStudents = students.filter(student =>
    student.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.apellidos.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.dni.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="students-container">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope font-bold text-4xl text-slate-900" data-testid="students-title">
              Alumnos
            </h1>
            <p className="font-inter text-slate-600 mt-2">
              Gestiona el registro de alumnos
            </p>
          </div>
          {canEdit && (
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button
                  onClick={openCreateDialog}
                  className="bg-[#004E89] hover:bg-[#003B66]"
                  data-testid="students-create-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Alumno
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle data-testid="student-dialog-title">
                    {editingStudent ? 'Editar Alumno' : 'Crear Nuevo Alumno'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="student-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nombre">Nombre *</Label>
                      <Input
                        id="nombre"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        required
                        data-testid="student-form-nombre"
                      />
                    </div>
                    <div>
                      <Label htmlFor="apellidos">Apellidos *</Label>
                      <Input
                        id="apellidos"
                        value={formData.apellidos}
                        onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                        required
                        data-testid="student-form-apellidos"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="dni">DNI/NIE/Pasaporte *</Label>
                    <Input
                      id="dni"
                      value={formData.dni}
                      onChange={(e) => setFormData({ ...formData, dni: e.target.value })}
                      required
                      data-testid="student-form-dni"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="student-form-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="telefono">Teléfono</Label>
                      <Input
                        id="telefono"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        data-testid="student-form-telefono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento</Label>
                      <Input
                        id="fecha_nacimiento"
                        type="date"
                        value={formData.fecha_nacimiento}
                        onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                        data-testid="student-form-fecha-nacimiento"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edad">Edad</Label>
                      <Input
                        id="edad"
                        type="number"
                        value={formData.edad}
                        onChange={(e) => setFormData({ ...formData, edad: e.target.value })}
                        data-testid="student-form-edad"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="direccion">Dirección</Label>
                    <Input
                      id="direccion"
                      value={formData.direccion}
                      onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                      data-testid="student-form-direccion"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="poblacion">Población</Label>
                      <Input
                        id="poblacion"
                        value={formData.poblacion}
                        onChange={(e) => setFormData({ ...formData, poblacion: e.target.value })}
                        data-testid="student-form-poblacion"
                      />
                    </div>
                    <div>
                      <Label htmlFor="codigo_postal">Código Postal</Label>
                      <Input
                        id="codigo_postal"
                        value={formData.codigo_postal}
                        onChange={(e) => setFormData({ ...formData, codigo_postal: e.target.value })}
                        data-testid="student-form-codigo-postal"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="profesion">Profesión</Label>
                    <Input
                      id="profesion"
                      value={formData.profesion}
                      onChange={(e) => setFormData({ ...formData, profesion: e.target.value })}
                      data-testid="student-form-profesion"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="student-form-cancel">
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-[#004E89] hover:bg-[#003B66]" data-testid="student-form-submit">
                      {editingStudent ? 'Actualizar' : 'Crear'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar alumnos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="students-search-input"
          />
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="students-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Nombre y Apellidos
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    DNI/NIE
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Teléfono
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                      No hay alumnos registrados
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((student) => (
                    <tr
                      key={student.id}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`student-row-${student.id}`}
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">{student.nombre} {student.apellidos}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">{student.dni}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">{student.email || '-'}</td>
                      <td className="px-6 py-4 text-sm text-slate-700">{student.telefono || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {canEdit && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(student)}
                                data-testid={`student-edit-${student.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(student.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`student-delete-${student.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Students;
